import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { STTG_CTMS } from 'constants/study/studyTypes';
import { union } from 'lodash/array';
import { isEmpty, isNull } from 'lodash/lang';
import moment from 'moment/moment';
import { toFormattedBill } from 'services/financial';
import { onRequestError } from 'services/handlers';
import { NS_COMMON, NSC_STIPEND, NSPP_STIPEND } from 'services/i18n/namespaces';

import { stipendStatusMap } from 'components/pages/finance/PatientStipendWorklist/constants';

import PatientStipendApi from '../../../../../../../../api/patient/PatientStipendApi';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../../../common/general/Button';

import './PaidStipendItemModal.scss';

function PaidStipendItemModal({ modalBox, viewOnlyMod, encounter, itemsData, onStipendPay, patientInfo, studyType }) {
  const { t } = useTranslation();
  const { ssuPatientId } = useParams();
  const { encounterName, originalEncounterName, sitePatientEncounterId } = encounter;
  const [encounterDate, setEncounterDate] = useState(moment(encounter?.encounterDate || undefined));
  const [notConfirmedEncounterDate, setNotConfirmedEncounterDate] = useState(null);
  const isEncounterDateUpdatedRef = useRef(false);
  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set([])
  });

  const selectedIds = useMemo(() => {
    return Array.from(rowSelectionModel.ids);
  }, [rowSelectionModel]);

  const columns = useMemo(() => {
    return [
      { field: 'eventNumber', headerName: t(`${NSPP_STIPEND}:modal.table.eventId`), width: 80 },
      { field: 'itemGroupName', headerName: t(`${NSPP_STIPEND}:modal.table.description`), width: 200 },
      {
        field: 'amount',
        headerName: t(`${NS_COMMON}:amount`),
        width: 120,
        valueFormatter(value) {
          return toFormattedBill(value);
        }
      },
      {
        field: 'eventItemStatus',
        headerName: t(`${NSPP_STIPEND}:modal.table.paymentStatus`),
        flex: 1,
        minWidth: 185,
        renderCell({ value }) {
          return (
            <div className="overflow-ellipsis">
              {value ? t(`${NSC_STIPEND}:stipendEventItemStatuses.${stipendStatusMap[value]}`) : '—'}
            </div>
          );
        }
      }
    ];
  }, [t]);

  const allowedSelectedRows = useMemo(
    function() {
      if (isEmpty(itemsData) || isEmpty(selectedIds)) {
        return null;
      }
      return itemsData.filter(function(row) {
        if (row.eventItemStatus !== 'PROJECTED') {
          return false;
        }
        return selectedIds.includes(getRowId(row));
      });
    },
    [itemsData, selectedIds]
  );

  useEffect(
    function() {
      if (isEmpty(itemsData)) {
        return;
      }
      const selectedRowIdsByDefault = itemsData
        .filter(function({ eventItemStatus }) {
          return eventItemStatus !== 'PROJECTED';
        })
        .map(row => getRowId(row));
      if (isEmpty(selectedRowIdsByDefault)) {
        return;
      }
      setRowSelectionModel(function(state) {
        return {
          ...state,
          ids: new Set(union(Array.from(state.ids), selectedRowIdsByDefault))
        };
      });
    },
    [itemsData]
  );

  const cardIsActive = !!patientInfo?.cardInfo?.registered;
  const encounterDateIsFuture = encounterDate.isAfter(moment());
  const submitButtonDisabled = !allowedSelectedRows?.length || !cardIsActive || encounterDateIsFuture;
  const displayedEncounterDate = notConfirmedEncounterDate ?? encounterDate;
  const isCheckedInEncounter = !isNull(sitePatientEncounterId);

  function onEncounterDateUpdate(date) {
    if (isCheckedInEncounter) {
      setNotConfirmedEncounterDate(date);
      ModalBoxes.confirm({
        title: t('confirmation'),
        content: t(`${NSPP_STIPEND}:modal.changeEncounterDateConfirmMessage`),
        confirmButton: t('yes'),
        cancelButton: t('no')
      })
        .then(
          () =>
            PatientStipendApi.updateSitePatientEncounterDate(ssuPatientId, sitePatientEncounterId, date).then(
              function() {
                isEncounterDateUpdatedRef.current = true;
                setEncounterDate(date);
              },
              onRequestError
            ),
          () => {}
        )
        .finally(() => setNotConfirmedEncounterDate(null));
    } else {
      setEncounterDate(date);
    }
  }

  return (
    <>
      <ModalBoxes.Header className="modal-header" onClose={onClose}>
        {viewOnlyMod ? t(`${NSPP_STIPEND}:modal.paidStipend`) : t(`${NSPP_STIPEND}:modal.payStipend`)}
      </ModalBoxes.Header>
      <ModalBoxes.Body className="modal-body">
        <Grid container spacing={2}>
          <Grid size={4}>
            <Typography variant="body1" className="encounter-name">
              {isCheckedInEncounter
                ? originalEncounterName + ': ' + encounterDate.format(DD_SLASH_MMM_SLASH_YYYY)
                : encounterName}
            </Typography>
          </Grid>
          <Grid size={4} />
          <Grid size={4}>
            <DesktopDatePicker
              label={t(`${NSPP_STIPEND}:modal.encounterDatePickerLabel`)}
              value={moment(displayedEncounterDate)}
              onChange={onEncounterDateUpdate}
              format={DD_SLASH_MMM_SLASH_YYYY}
              maxDate={moment()}
              disabled={isEncounterDateDisabled(viewOnlyMod, sitePatientEncounterId, studyType)}
              views={['year', 'month', 'day']}
              slotProps={{
                textField: {
                  variant: 'standard',
                  className: 'date-picker',
                  error: studyType === STTG_CTMS && encounterDateIsFuture,
                  helperText:
                    studyType === STTG_CTMS &&
                    encounterDateIsFuture &&
                    t(`${NSPP_STIPEND}:modal.datePickerFutureDateError`)
                }
              }}
            />
          </Grid>
          {!cardIsActive && (
            <Grid size={12}>
              <Typography variant="body1" color="error">
                {t(`${NSPP_STIPEND}:modal.noValidPid`)}
              </Typography>
            </Grid>
          )}
          <Grid size={12}>
            <DataGridPremium
              getRowId={getRowId}
              apiRef={apiRef}
              rows={itemsData || []}
              columns={columns}
              className="items-table"
              checkboxSelection={!viewOnlyMod}
              onRowSelectionModelChange={function(newRowSelectionModel) {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowSelectionModel={rowSelectionModel}
              isRowSelectable={params => params.row.eventItemStatus === 'PROJECTED'}
              disableSelectionOnClick={viewOnlyMod}
              disableRowSelectionOnClick={viewOnlyMod}
              autoHeight
              disableRowGrouping
              disableAggregation
              disableRowSelectionExcludeModel
            />
          </Grid>
        </Grid>
      </ModalBoxes.Body>
      {!viewOnlyMod && (
        <ModalBoxes.Footer>
          <Button
            disabled={submitButtonDisabled}
            priority="high"
            onClick={() => {
              onStipendPay(encounter, encounterDate, allowedSelectedRows);
              modalBox.close();
            }}
          >
            {t(`${NSPP_STIPEND}:modal.payStipendsButton`)}
          </Button>
        </ModalBoxes.Footer>
      )}
    </>
  );

  function onClose() {
    modalBox.close(isEncounterDateUpdatedRef.current);
  }
}

function getRowId({ eventItemId, eventName, itemGroupKey, budgetRowId }) {
  return `${eventItemId || eventName + itemGroupKey}${budgetRowId}`;
}

PaidStipendItemModal.className = 'paid-stipend-item-modal';
PaidStipendItemModal.size = 'w800';

export default PaidStipendItemModal;

function isEncounterDateDisabled(viewOnlyMod, sitePatientEncounterId, studyType) {
  if (viewOnlyMod) {
    return true;
  }
  if (studyType !== STTG_CTMS) {
    return !!sitePatientEncounterId;
  }
  return false;
}
